import {
  LeistungEigenschaft,
  Produktlinie,
  selectIndicesOfDeactivatedProduktlinien,
  UebergreifendItem,
  WEITERE_NATURGEFAHREN_ID,
  ZusatzoptionItem,
  ZUSATZOPTION_ID_TRAGERISIKO,
} from '@ps20-frontend/frontend-models';
import {
  ExtremwetterUWProcessQuery,
  isExtremwetterUWProcessNecessary,
} from '@ps20-frontend/selector';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Feature, HausArt, Produktlinienname, Stockwerk } from '@ps20/bff-models';
import { IAppState } from '../../../store/appState';
import {
  VersicherungssummeEnum,
  VertragsoptionenVHV,
} from '../../../store/model/vertragsoptionen.model';
import {
  ZusatzoptionenNameVHVEnum,
  ZusatzoptionItemVHV,
} from '../../../store/model/zusatzoptionenVHV.model';
import { VersicherungsSummeGrenzen } from '../../../store/model/versicherungssummen';
import {
  ZUSATZOPTION_FAHRRAD_ID, ZUSATZOPTION_FAHRRAD_KASKO_ID
} from '../../../store/model/fahradwertForLinie.model';
import {
  getEntschaedigungsgrenzeInEuro,
  isTragerisikoComplete,
  TragerisikoWerte,
} from '../../../store/model/tragerisiko.model';
import { EntschaedigungsgrenzeRadForLinie } from '../../../store/model/entschaedigungsgrenzeRad.model';
import { selectStaendigBewohntBoolean } from './versObjekt.selector';
import { selectIsErsatzgeschaeft } from './vertragsoption.selector';

export const selectSelectedProduktlinie = (state: IAppState): Produktlinie =>
  getSelectedProduktlinie(state.produkt);

export function mapStateToExtremwetterUWProcessQuery(
  state: IAppState,
): ExtremwetterUWProcessQuery {
  return {
    elementarSelbstbehaltId: state.elementarSelbstbehalt?.id,
    activateElementar: state.vorbelegungen?.activateElementar,
    flutzone: state.gisDaten?.flutzone,
    featureToggles: state.transientState?.featureToggles,
    vorschaedenVorhanden: state.vorschaeden?.vorhanden,
    vertragsoptionen: state.vertragsoptionen ?? ({} as VertragsoptionenVHV),
    elementarVorschadenCountFromRisikoortService: 0,
    risikoortZuerszone: state.risikoort?.zuerszone,
    elementarVorschaedenCount: state.vorschaeden?.elementar,
  };
}

export const isMehrfamilienhausObergeschoss = (state: IAppState): boolean => {
  return (
    state.versObjekt?.hausart === HausArt.MEHRFAMILIENHAUS &&
    state.versObjekt?.stockwerk === Stockwerk.OBERGESCHOSS
  );
};

export const selectHasWeitereNaturgefahrenSelected = (state: IAppState): boolean =>
  selectSelectedProduktlinie(state)?.zusatzoptionen?.some(
    zusatzoption => zusatzoption.id === WEITERE_NATURGEFAHREN_ID && zusatzoption.isSelected,
  ) === true;

export const selectExtremwetterRisk = (state: IAppState): boolean =>
  selectHasWeitereNaturgefahrenSelected(state) &&
  isExtremwetterUWProcessNecessary(mapStateToExtremwetterUWProcessQuery(state)) &&
  !isMehrfamilienhausObergeschoss(state);

export const selectIsExtremwetterUWProcessNecessary = (state: IAppState): boolean => {
  if (!state.transientState?.featureToggles?.[Feature.VHV_UW]) {
    return false;
  }
  return selectExtremwetterRisk(state);
};

export const selectZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItemVHV> => {
  const selectedProduct: Produktlinie = selectSelectedProduktlinie(state);
  const selectedZusatzoptionen: ZusatzoptionItemVHV[] =
    selectSelectedProductZusatzoptionItems(state);
  const entschaedigungsgrenzeRad: EntschaedigungsgrenzeRadForLinie[] =
    state?.entschaedigungsgrenzeRad;
  const tragerisiko: TragerisikoWerte = selectTragerisiko(state);

  return selectedZusatzoptionen.map(zusatzoption => {
    if (
      zusatzoption.id === ZUSATZOPTION_FAHRRAD_ID ||
      zusatzoption.id === ZUSATZOPTION_FAHRRAD_KASKO_ID
    ) {
      const selectedEntschaedigungsgrenze = entschaedigungsgrenzeRad?.find(
        x => x.produktlinie === selectedProduct.produktName,
      )?.betrag;
      return {
        ...zusatzoption,
        eigenschaft: selectedEntschaedigungsgrenze ? String(selectedEntschaedigungsgrenze) : '',
      };
    } else if (zusatzoption.id === ZUSATZOPTION_ID_TRAGERISIKO) {
      return {
        ...zusatzoption,
        entschaedigungsgrenze: getEntschaedigungsgrenzeInEuro(tragerisiko),
        einzelwert: tragerisiko?.einzelwert,
      };
    } else {
      return zusatzoption;
    }
  });
};

const selectSelectedProductZusatzoptionItems = (state: IAppState): ZusatzoptionItem[] =>
  getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];

export const selectProduktlinien = (state: IAppState): Array<Produktlinie> =>
  state.produkt?.produktlinien || [];

export const selectUebergreifendeLeistungen = (state: IAppState): Array<UebergreifendItem> =>
  state.produkt?.uebergreifend || [];

export const selectSelectedZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  return selectSelectedProductZusatzoptionItems(state).filter(elem => elem.isSelected);
};

export const selectShowPremiumNotAllowedVersicherungssumme = (state: IAppState): boolean => {
  const versicherungssummeXL = state.versicherungssummen.find(
    versicherungssumme => versicherungssumme.produktlinie === Produktlinienname.PREMIUM,
  );

  if (!versicherungssummeXL) {
    return false;
  }

  return versicherungssummeXL.summe > VersicherungsSummeGrenzen.MAX_VERSSUMME;
};

export const selectNichtStaendigBewohntUeber50000 = (state: IAppState): boolean => {
  return (
    state.vertragsoptionen?.versicherungssumme >
      VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT &&
    !selectStaendigBewohntBoolean(state)
  );
};

const isVsuOver500kAndStaendigBewohnt = (state: IAppState): boolean =>
  (state.vertragsoptionen?.versicherungssumme > VersicherungsSummeGrenzen.MAX_VERSSUMME ||
    selectShowPremiumNotAllowedVersicherungssumme(state)) &&
  selectStaendigBewohntBoolean(state);

export const selectStaendigBewohntUeber500000 = (state: IAppState): boolean =>
  isVsuOver500kAndStaendigBewohnt(state) && !selectIsErsatzgeschaeft(state);

export const selectStaendigBewohntUeber500000InErsatzgeschaeft = (state: IAppState): boolean =>
  isVsuOver500kAndStaendigBewohnt(state) && selectIsErsatzgeschaeft(state);

export const selectTransientStateWertsachensumme = (state: IAppState): number | undefined => {
  if (
    state.vertragsoptionen?.versicherungssummeArt === VersicherungssummeEnum.BISHERIGE_VERSSUMME
  ) {
    return state?.transientState?.relevantVorbelegungdaten?.wertsachensumme;
  } else {
    const selectedProductline = selectSelectedProduktlinie(state);
    if (state.wertsachensummen && selectedProductline) {
      return state.wertsachensummen.find(
        wertsachensumme => wertsachensumme.produktline === selectedProductline.produktName,
      )?.summe;
    } else {
      return undefined;
    }
  }
};

export const selectMaxInsurableSumExceeded = (state: IAppState): boolean => {
  if (!selectStaendigBewohntBoolean(state)) {
    return false;
  }
  const selectedProductline = selectSelectedProduktlinie(state);
  const wertsachensumme = selectTransientStateWertsachensumme(state);
  if (wertsachensumme && selectedProductline) {
    const currentProductLine = state?.wertsachensummen?.find(
      element => element.produktline === selectedProductline.produktName,
    );
    const selectedMaxValue = currentProductLine?.maxSumme;
    return wertsachensumme > selectedMaxValue;
  }
  return false;
};

export const selectNichtStaendigBewohntUnter50000 = (state: IAppState): boolean => {
  return (
    state.vertragsoptionen?.versicherungssumme <=
      VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT &&
    !selectStaendigBewohntBoolean(state)
  );
};
export const selectVertragsoptionen = (state: IAppState): VertragsoptionenVHV =>
  state.vertragsoptionen;

export const selectVertragsoptionSelectedProduktlinie = (state: IAppState): VertragsoptionenVHV => {
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);

  if (state.versicherungssummen.length === 0 || !ausgewaehlteProduktlinie) {
    return state.vertragsoptionen;
  }

  const versicherungssummeGewaehlteLinie = state.versicherungssummen
    .find(
      versicherungsumme => versicherungsumme.produktlinie === ausgewaehlteProduktlinie.produktName,
    )?.summe;

  return {
    ...state.vertragsoptionen,
    versicherungssumme: versicherungssummeGewaehlteLinie ?? state.vertragsoptionen.versicherungssumme,
  };
};

export const selectGesamtbeitrag = (state: IAppState): number => state.gesamtbeitrag;

export const selectSelectedProduktlinieIndex = (state: IAppState): number => {
  return state.produkt?.produktlinien.findIndex(produktlinie => produktlinie.selected);
};

export const selectProduktlinienForWohnung = (state: IAppState): Array<Produktlinie> => {
  const selectedProduktLinien: Array<Produktlinie> = selectProduktlinien(state);
  if (!state.versObjekt.staendigBewohnt) {
    selectedProduktLinien.forEach(produktlinie =>
      produktlinie.leistungen.forEach(leistung => {
        if (
          [
            'BargeldUndAufGeldkartenGeladeneBetraege',
            'bankschliessfach',
            'wertsachenaufbrechen',
          ].includes(leistung.id)
        ) {
          leistung.eigenschaft = LeistungEigenschaft.EXKLUSIVE;
        }
      }),
    );
  }
  return selectedProduktLinien;
};
export const selectVersicherungssummeText = (state: IAppState): string => {
  return selectProduktlinienForWohnung(state)[0]?.leistungen.find(
    lst => lst.id === 'versicherungssumme',
  )?.info;
};

export const selectProduktlinieIndexesToDeactivate = (state: IAppState): number[] => {
  const indexesOfDeactivatedProduktlinien = new Set(selectIndicesOfDeactivatedProduktlinien(state));
  const produktlinien = selectProduktlinienForWohnung(state);
  const premiumProduktlinie = state.versicherungssummen.find(
    versicherungssume => versicherungssume.produktlinie === Produktlinienname.PREMIUM,
  );

  if (
    premiumProduktlinie?.summe > VersicherungsSummeGrenzen.MAX_VERSSUMME &&
    !selectIsErsatzgeschaeft(state)
  ) {
    indexesOfDeactivatedProduktlinien.add(
      produktlinien.findIndex(produklinie => produklinie.produktName === Produktlinienname.PREMIUM),
    );
  }
  return [...indexesOfDeactivatedProduktlinien];
};

export const selectElementarbaustein = (state: IAppState): ZusatzoptionItemVHV => {
  return selectZusatzoptionItems(state).find(
    item => item.name === ZusatzoptionenNameVHVEnum.ELEMENTAR,
  );
};

export const selectTragerisiko = (state: IAppState): TragerisikoWerte => state.tragerisiko;

export const selectIsTragerisikoComplete = (state: IAppState): boolean =>
  isTragerisikoComplete(selectTragerisiko(state));
